import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { MapPin, Navigation, Search, X, Check, Loader2 } from "lucide-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { getParentProfile } from "@/services/parent";
import toast from "react-hot-toast";

const LIBRARIES: ("places")[] = ["places"];
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const schoolIconUrl =
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="40" height="48" viewBox="0 0 40 48">
  <filter id="s"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity=".3"/></filter>
  <g filter="url(#s)">
    <path d="M20 2C11.16 2 4 9.16 4 18c0 12 16 28 16 28s16-16 16-28C36 9.16 28.84 2 20 2z" fill="#2563eb"/>
    <circle cx="20" cy="18" r="11" fill="white"/>
  </g>
  <g transform="translate(12,10)">
    <polygon points="8,0 16,5 0,5" fill="#2563eb"/>
    <rect x="2" y="5" width="12" height="8" fill="#2563eb"/>
    <rect x="5.5" y="9" width="3" height="4" fill="white"/>
    <rect x="2.5" y="6.5" width="2.5" height="2" fill="white" rx="0.3"/>
    <rect x="11" y="6.5" width="2.5" height="2" fill="white" rx="0.3"/>
  </g>
</svg>`);

const homeIconUrl =
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="40" height="48" viewBox="0 0 40 48">
  <filter id="h"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity=".3"/></filter>
  <g filter="url(#h)">
    <path d="M20 2C11.16 2 4 9.16 4 18c0 12 16 28 16 28s16-16 16-28C36 9.16 28.84 2 20 2z" fill="#16a34a"/>
    <circle cx="20" cy="18" r="11" fill="white"/>
  </g>
  <g transform="translate(12,10)">
    <polygon points="8,1 15,6 1,6" fill="#16a34a"/>
    <rect x="2" y="6" width="12" height="7" fill="#16a34a"/>
    <rect x="5.5" y="9" width="3" height="4" fill="white"/>
    <rect x="2.5" y="7" width="2.5" height="2" fill="white" rx="0.3"/>
    <rect x="11" y="7" width="2.5" height="2" fill="white" rx="0.3"/>
  </g>
</svg>`);

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (data: { latitude: string; longitude: string; address: string; distanceKm?: number }) => void;
    value?: { latitude: string; longitude: string };
    schoolLat?: number;
    schoolLng?: number;
    schoolName?: string;
    parentUsername?: string;
    selectedState?: string;
    selectedCity?: string;
}

function parseAddressComponents(
    components: google.maps.GeocoderAddressComponent[],
    formatted: string
): { main: string; sub: string } {
    const get = (type: string) =>
        components.find((c) => c.types.includes(type))?.long_name || "";

    const premise = get("premise");
    const streetNumber = get("street_number");
    const route = get("route");
    const area = get("sublocality_level_1") || get("sublocality") || get("neighborhood");
    const locality = get("locality");
    const adminArea = get("administrative_area_level_1");

    const mainParts = [premise || streetNumber, route].filter(Boolean);
    const main = mainParts.length > 0 ? mainParts.join(", ") : area || formatted.split(",")[0].trim();
    const sub = [area, locality, adminArea].filter(Boolean).join(", ");
    return { main, sub };
}

function MapLoader({ message }: { message: string }) {
    return (
        <div className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden bg-[#e8ecef]">
            <div
                className="absolute inset-0 opacity-40"
                style={{
                    backgroundImage:
                        "linear-gradient(#c5cdd4 1px, transparent 1px), linear-gradient(90deg, #c5cdd4 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                }}
            />
            <div className="absolute inset-0 opacity-20">
                <div className="absolute top-[30%] left-0 right-0 h-6 bg-white rounded" />
                <div className="absolute top-[60%] left-0 right-0 h-4 bg-white rounded" />
                <div className="absolute top-0 bottom-0 left-[35%] w-5 bg-white rounded" />
                <div className="absolute top-0 bottom-0 left-[70%] w-3 bg-white rounded" />
            </div>
            <div className="relative z-10 flex flex-col items-center gap-4">
                <div className="relative">
                    <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center shadow-2xl border-4 border-white animate-bounce">
                        <MapPin className="w-8 h-8 text-white fill-current" />
                    </div>
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-orange-500 rounded-full" />
                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-8 h-2 bg-black/20 rounded-full blur-sm" />
                </div>
                <div className="bg-white/95 px-5 py-2.5 rounded-full shadow-lg flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
                    <p className="text-sm font-semibold text-gray-700">{message}</p>
                </div>
            </div>
        </div>
    );
}

export default function FullScreenLocationPicker({ isOpen, onClose, onConfirm, value, schoolLat, schoolLng, schoolName, parentUsername: parentUsernameProp, selectedState,
    selectedCity, }: Props) {
    const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "")
        .replace(/^'|'$/g, "")
        .replace(/^"|"$/g, "");
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: apiKey,
        libraries: LIBRARIES,
    });

    const [map, setMap] = useState<google.maps.Map | null>(null);
    const mapRef = useRef<google.maps.Map | null>(null);

    // Two-phase init: detect GPS location FIRST, then render map at that spot
    const [mapInitCenter, setMapInitCenter] = useState<{ lat: number; lng: number } | null>(null);
    const [showMap, setShowMap] = useState(false);
    const [loaderMsg, setLoaderMsg] = useState("Detecting your location…");
    const geoStartedRef = useRef(false);
    // showMapRef prevents late GPS callback from changing mapInitCenter (which would re-pan the map)
    const showMapRef = useRef(false);
    const pendingGeoCenter = useRef<{ lat: number; lng: number } | null>(null);

    // Center in a ref — no state = no re-render = no center-prop feedback loop
    const currentCenterRef = useRef(DEFAULT_CENTER);
    const [displayCoords, setDisplayCoords] = useState<{ lat: number; lng: number } | null>(null);
    // Only draw route when user explicitly selects a point (not from API/initial value auto-center)
    const [routeEnabled, setRouteEnabled] = useState(false);
    // Imperative renderer — avoids @react-google-maps/api cleanup bug that leaves stale polylines
    const dirRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
    const [duration, setDuration] = useState("")
    const [distance, setDistance] = useState("")
    const [manualLat, setManualLat] = useState("");
    const [manualLng, setManualLng] = useState("");

    const clearRenderer = useCallback(() => {
        if (dirRendererRef.current) {
            dirRendererRef.current.setMap(null);
            dirRendererRef.current = null;
        }
        setDistance("");
        setDuration("");
    }, []);

    // Address
    const [address, setAddress] = useState("");
    const [addressMain, setAddressMain] = useState("");
    const [addressSub, setAddressSub] = useState("");
    const [loadingAddress, setLoadingAddress] = useState(false);
    const userDetails = useCurrentUser();
    const username = parentUsernameProp || userDetails?.username

    // UI
    const [isDragging, setIsDragging] = useState(false);
    const [locating, setLocating] = useState(false);
    const [parentProfile, setParentProfile] = useState<any>(null)
    const parentProfileRef = useRef<any>(null);
    const currentRoadDistanceRef = useRef<number | null>(null);

    const schoolDistance = useMemo(() => {
        if (!displayCoords || schoolLat == null || schoolLng == null) return null;
        const d = haversineKm(schoolLat, schoolLng, displayCoords.lat, displayCoords.lng);
        return d < 1 ? `${Math.round(d * 1000)} m` : `${d.toFixed(1)} km`;
    }, [displayCoords, schoolLat, schoolLng]);

    // Imperative route calculation — cancels in-flight requests and removes old polyline immediately
    useEffect(() => {
        if (!isLoaded || !displayCoords || schoolLat == null || schoolLng == null || !routeEnabled) {
            clearRenderer();
            return;
        }
        // Remove previous polyline before starting new request
        clearRenderer();
        let cancelled = false;
        const svc = new google.maps.DirectionsService();
        svc.route(
            {
                origin: { lat: displayCoords.lat, lng: displayCoords.lng},
                destination: {lat: schoolLat, lng: schoolLng   },
                travelMode: google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
                if (cancelled) return;
                if (status === google.maps.DirectionsStatus.OK && result && mapRef.current) {
                    dirRendererRef.current = new google.maps.DirectionsRenderer({
                        suppressMarkers: true,
                        polylineOptions: { strokeColor: "#2563eb", strokeWeight: 5, strokeOpacity: 0.85 },
                        map: mapRef.current,
                        directions: result,
                    });
                    const meters = result.routes[0].legs[0].distance?.value; // meters
                    if (meters != null) {
                        const km = meters / 1000;
                        setDistance(result.routes[0].legs[0].distance?.text || "");
                        setDuration(result.routes[0].legs[0].duration?.text || "");

                        // 🔥 Store numeric road distance
                        currentRoadDistanceRef.current = km;
                    }
                    setDuration(result.routes[0].legs[0].duration?.text || "");
                    // Fit map to show school + home
                    const bounds = new google.maps.LatLngBounds();
                    bounds.extend({ lat: schoolLat, lng: schoolLng });
                    bounds.extend({ lat: displayCoords.lat, lng: displayCoords.lng });
                    mapRef.current.fitBounds(bounds, 80);
                }
            }
        );
        return () => { cancelled = true; clearRenderer(); };
    }, [displayCoords, schoolLat, schoolLng, isLoaded, routeEnabled, clearRenderer]);
    // Autocomplete
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const nativeAcRef = useRef<google.maps.places.Autocomplete | null>(null);
    const geocodeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const geocodedOnce = useRef(false);
const geocodeRequestRef = useRef(0);
    // Keep mapRef in sync (needed for stable closures in autocomplete listener)
    useEffect(() => { mapRef.current = map; }, [map]);

    // Body scroll lock + autocomplete dropdown z-index fix
    useEffect(() => {
        if (!isOpen) return;
        document.body.style.overflow = "hidden";
        const style = document.createElement("style");
        style.id = "pac-overlay";
        style.textContent = ".pac-container { z-index: 100000 !important; }";
        document.head.appendChild(style);
        return () => {
            document.body.style.overflow = "";
            document.getElementById("pac-overlay")?.remove();
        };
    }, [isOpen]);

    const loadProfile = useCallback(async () => {
        if (!username || username === 'User') return;
        //   setProfileLoading(true);
        try {
            const response = await getParentProfile(username);
            if (response.status === 200 && response.data) {
                const data = Array.isArray(response.data) ? (response.data as any[])[0] : response.data;
                setParentProfile(data);
                //   const incomplete = !data?.Latitude || !data?.Longitude;
                //   setProfileIncomplete(incomplete);
                //   if (incomplete) setShowUpdateProfile(true);
            }
        } catch (error) {
            // console.error('Failed to load profile:', error);
        } finally {
            // setProfileLoading(false);
        }
    }, [username]);

    // Keep ref in sync so Phase 1 closure always sees latest profile
    useEffect(() => { parentProfileRef.current = parentProfile; }, [parentProfile]);

    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    useEffect(() => { parentProfileRef.current = parentProfile; }, [parentProfile]);

    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    // Phase 1 — detect GPS before showing map (avoids "flying from India" animation)
    useEffect(() => {
        if (!isOpen) {
            geoStartedRef.current = false;
            showMapRef.current = false;
            geocodedOnce.current = false;
            pendingGeoCenter.current = null;
            setShowMap(false);
            setMapInitCenter(null);
            setLoaderMsg("Detecting your location…");
            return;
        }
        if (geoStartedRef.current) return;
        geoStartedRef.current = true;

        const openMapAt = (loc: { lat: number; lng: number }) => {
            currentCenterRef.current = loc;
            if (showMapRef.current) {
                // Map already visible — move programmatically.
                // Never change mapInitCenter here — that's the center prop and would trigger @react-google-maps/api to re-pan.
                if (mapRef.current) {
                    mapRef.current.setCenter(loc);
                    mapRef.current.setZoom(17);
                    setDisplayCoords(loc);
                } else {
                    // Map is rendering but not yet loaded — queue it for Phase 2
                    pendingGeoCenter.current = loc;
                    setDisplayCoords(loc);
                }
                return;
            }
            showMapRef.current = true;
            setMapInitCenter(loc);
            setShowMap(true);
        };

        // Priority 1: currently selected location from parent form
const valLat = parseFloat(value?.latitude ?? "");
const valLng = parseFloat(value?.longitude ?? "");

if (!isNaN(valLat) && !isNaN(valLng)) {
    openMapAt({
        lat: valLat,
        lng: valLng,
    });
    return;
}

// Priority 2: CURRENT GPS LOCATION
setLoaderMsg("Detecting your location…");

const MIN_ACCURACY = 200;
const MAX_WAIT = 20000;

let watchId: number;

const timeout = setTimeout(() => {
    navigator.geolocation.clearWatch(watchId);

    // Only use profile location as fallback
    const profile = parentProfileRef.current;

    const savedLat = parseFloat(profile?.Latitude);
    const savedLng = parseFloat(profile?.Longitude);

    if (!isNaN(savedLat) && !isNaN(savedLng)) {
        openMapAt({
            lat: savedLat,
            lng: savedLng,
        });
    } else {
        openMapAt(DEFAULT_CENTER);
    }
}, MAX_WAIT);

watchId = navigator.geolocation.watchPosition(
    ({ coords }) => {

        if (coords.accuracy > MIN_ACCURACY) {
            return;
        }

        clearTimeout(timeout);
        navigator.geolocation.clearWatch(watchId);

        const loc = {
            lat: coords.latitude,
            lng: coords.longitude,
        };

        openMapAt(loc);
    },
    () => {
        clearTimeout(timeout);
        navigator.geolocation.clearWatch(watchId);

        // GPS failed → use saved profile location
        const profile = parentProfileRef.current;

        const savedLat = parseFloat(profile?.Latitude);
        const savedLng = parseFloat(profile?.Longitude);

        if (!isNaN(savedLat) && !isNaN(savedLng)) {
            openMapAt({
                lat: savedLat,
                lng: savedLng,
            });
        } else {
            openMapAt(DEFAULT_CENTER);
        }
    },
    {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: MAX_WAIT,
    }
);

return () => {
    clearTimeout(timeout);
    navigator.geolocation.clearWatch(watchId);
};
    }, [isOpen]);

   const reverseGeocode = useCallback((lat: number, lng: number) => {
    const requestId = ++geocodeRequestRef.current;

    setLoadingAddress(true);

    new google.maps.Geocoder().geocode(
        {
            location: { lat, lng },
            region: "IN",
        },
        (results, status) => {

            // Ignore old/stale geocoder responses
            if (requestId !== geocodeRequestRef.current) {
                return;
            }

            setLoadingAddress(false);

            if (status === "OK" && results && results.length > 0) {

                const priorityTypes = [
                    "street_address",
                    "premise",
                    "subpremise",
                    "route",
                    "neighborhood",
                    "sublocality",
                    "locality",
                ];

                let bestResult = results.find((r) =>
                    r.types.some((t) => priorityTypes.includes(t))
                );

                if (!bestResult) {
                    bestResult =
                        results.find((r) =>
                            r.types.includes("plus_code")
                        ) || results[0];
                }

                const full = bestResult.formatted_address;

                const { main, sub } = parseAddressComponents(
                    bestResult.address_components,
                    full
                );

                setAddress(full);
                setAddressMain(main);
                setAddressSub(sub);

            } else {

                const fb = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

                setAddress(fb);
                setAddressMain(fb);
                setAddressSub("");
            }
        }
    );
}, []);

    // Phase 2 — geocode the starting position once the map instance is ready
    useEffect(() => {
        if (!map || !mapInitCenter || geocodedOnce.current) return;
        geocodedOnce.current = true;
        // Apply any pending GPS center (arrived between showMap=true and map load)
        if (pendingGeoCenter.current) {
            map.setCenter(pendingGeoCenter.current);
            map.setZoom(17);
            currentCenterRef.current = pendingGeoCenter.current;
            pendingGeoCenter.current = null;
        }
        const c = currentCenterRef.current;
        setDisplayCoords(c);
        if (schoolLat != null && schoolLng != null) {
            setRouteEnabled(true);
        }
        reverseGeocode(c.lat, c.lng);
    }, [map, mapInitCenter, reverseGeocode]);

    const validateLocationByComponents = (
        components: google.maps.GeocoderAddressComponent[]
    ) => {
        //     if (!selectedCity || !selectedState) {
        //     toast.error(
        //         "Please select State and District first"
        //     );

        //     return;
        // }
        const getComponent = (type: string) =>
            components.find((c) =>
                c.types.includes(type)
            )?.long_name?.toLowerCase() || "";

        const normalize = (value: string) =>
            value
                .toLowerCase()
                .replace(/district/g, "")
                .replace(/ysr/g, "")
                .replace(/,/g, "")
                .replace(/\s+/g, " ")
                .trim();

        const selectedStateName = normalize(
            selectedState || ""
        );


        const placeState = normalize(
            getComponent(
                "administrative_area_level_1"
            ) || ""
        );

        const stateMatch =
            placeState === selectedStateName;

        return stateMatch;
    };

    // Autocomplete — native API, not the React component (which requires LoadScript context)
    useEffect(() => {
        if (
            !isOpen ||
            !isLoaded ||
            !searchInputRef.current
        ) {
            return;
        }

        if (!selectedCity || !selectedState) {
            toast.error(
                `Please select State and District`
            );
            return
        }

        // cleanup previous autocomplete
        if (nativeAcRef.current) {
            google.maps.event.clearInstanceListeners(
                nativeAcRef.current
            );

            nativeAcRef.current.unbindAll();

            nativeAcRef.current = null;
        }

        const createAutocomplete = (
            bounds?: google.maps.LatLngBounds
        ) => {
            const autocomplete =
                new google.maps.places.Autocomplete(
                    searchInputRef.current!,
                    {
                        fields: [
                            "geometry",
                            "formatted_address",
                            "address_components",
                        ],
                        types: ["geocode", "establishment"],
                        componentRestrictions: {
                            country: "in",
                        },
                    }
                );

            // Apply district/state restriction
            if (bounds) {
                autocomplete.setBounds(bounds);

                autocomplete.setOptions({
                    strictBounds: true,
                });
            }

            nativeAcRef.current = autocomplete;

            autocomplete.addListener(
                "place_changed",
                () => {
                    const place =
                        autocomplete.getPlace();

                    if (
                        !place?.geometry?.location
                    ) {
                        toast.error(
                            `Please search location inside ${selectedState}`
                        );
                        return;
                    }

                    const components =
                        place.address_components || [];


                    const getComponent = (
                        types: string[]
                    ) => {
                        const comp =
                            components.find((c) =>
                                types.some((t) =>
                                    c.types.includes(t)
                                )
                            );

                        return (
                            comp?.long_name || ""
                        )
                            .toLowerCase()
                            .trim();
                    };

                    const normalize = (
                        value: string
                    ) =>
                        value
                            .toLowerCase()
                            .replace(/district/g, "")
                            .replace(/ysr/g, "")
                            .replace(/,/g, "")
                            .replace(/\s+/g, " ")
                            .trim();


                    const selectedStateName =
                        normalize(
                            selectedState || ""
                        );

                    const placeState =
                        normalize(
                            getComponent([
                                "administrative_area_level_1",
                            ])
                        );

                    const stateMatch =
                        placeState ===
                        selectedStateName;

                    if (
                        !stateMatch
                    ) {
                        toast.error(
                            `Please select location inside ${selectedState}`
                        );

                        // console.log(`Please select location inside ${selectedCity}, ${selectedState}`)

                        if (
                            searchInputRef.current
                        ) {
                            searchInputRef.current.value =
                                "";
                        }

                        return;
                    }

                    const lat =
                        place.geometry.location.lat();

                    const lng =
                        place.geometry.location.lng();

                    const loc = { lat, lng };

                    currentCenterRef.current =
                        loc;

                    setDisplayCoords(loc);

                    setRouteEnabled(true);

                    mapRef.current?.panTo(loc);

                    mapRef.current?.setZoom(18);

                    reverseGeocode(lat, lng);
                }
            );
        };

        // Restrict by district/state
        if (selectedState) {
            const geocoder =
                new google.maps.Geocoder();

            geocoder.geocode(
                {
                    address: `${selectedState}, India`,
                    region: "IN",
                },
                (results, status) => {
                    if (
                        status === "OK" &&
                        results?.[0]
                    ) {
                        createAutocomplete(
                            results[0].geometry.viewport
                        );
                    } else {
                        createAutocomplete();
                    }
                }
            );
        } else {
            createAutocomplete();
        }

        return () => {
            if (nativeAcRef.current) {
                google.maps.event.clearInstanceListeners(
                    nativeAcRef.current
                );

                nativeAcRef.current.unbindAll();

                nativeAcRef.current = null;
            }
        };
    }, [
        isOpen,
        isLoaded,
        selectedState,
        reverseGeocode,
    ]);
    // Reset on close
    useEffect(() => {
    if (!isOpen) {
        geocodeRequestRef.current++;

        nativeAcRef.current = null;

        if (geocodeDebounceRef.current) {
            clearTimeout(geocodeDebounceRef.current);
        }

        setAddress("");
        setAddressMain("");
        setAddressSub("");
        setDisplayCoords(null);
        setRouteEnabled(false);
        clearRenderer();
        setDistance("");
        setDuration("");
        setLocating(false);
        setIsDragging(false);
    }
}, [isOpen]);

    // Map click — drop/move the marker to wherever the user taps
    const handleMapClick = useCallback(
        (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return;

            const lat = e.latLng.lat();
            const lng = e.latLng.lng();

            const geocoder =
                new google.maps.Geocoder();

            geocoder.geocode(
                {
                    location: { lat, lng },
                    region: "IN",
                },
                (results, status) => {
                    if (
                        status !== "OK" ||
                        !results?.[0]
                    ) {
                        toast.error(
                            "Unable to validate location"
                        );

                        return;
                    }

                    const components =
                        results[0]
                            .address_components || [];

                    const isValidLocation =
                        validateLocationByComponents(
                            components
                        );

                    if (!isValidLocation) {
                        if (selectedState) {
                            toast.error(
                                `Please select a location inside ${selectedState}`
                            )
                        } else {
                            toast.error(
                                `Please select a Both State and District`
                            )
                        }

                        return;
                    }

                    setRouteEnabled(true);

                    currentCenterRef.current = {
                        lat,
                        lng,
                    };

                    setDisplayCoords({
                        lat,
                        lng,
                    });

                    reverseGeocode(lat, lng);
                }
            );
        },
        [
            reverseGeocode,
            selectedState,
        ]
    );

    const handleMyLocation = () => {
        if (!navigator.geolocation) return;

        setLocating(true);

        const MIN_ACCEPTABLE_ACCURACY = 200;
        const GOOD_ACCURACY = 80;
        const MAX_WAIT_MS = 20000;

        let watchId: number;

        const stop = () => {
            navigator.geolocation.clearWatch(watchId);
            setLocating(false);
        };

        const timeout = setTimeout(() => {
            stop();
        }, MAX_WAIT_MS);

        watchId = navigator.geolocation.watchPosition(
            ({ coords }) => {
                // Ignore inaccurate IP fixes
                if (coords.accuracy > MIN_ACCEPTABLE_ACCURACY) return;

                const loc = {
                    lat: coords.latitude,
                    lng: coords.longitude,
                };

                currentCenterRef.current = loc;
                setDisplayCoords(loc);
                setRouteEnabled(true);

                mapRef.current?.setCenter(loc);
                mapRef.current?.setZoom(18);

                reverseGeocode(loc.lat, loc.lng);

                if (coords.accuracy <= GOOD_ACCURACY) {
                    clearTimeout(timeout);
                    stop();
                }
            },
            () => stop(),
            {
                enableHighAccuracy: true,
                maximumAge: 0,
                timeout: MAX_WAIT_MS,
            }
        );
    };

    const handleLatLngSearch = () => {
        const lat = parseFloat(manualLat);
        const lng = parseFloat(manualLng);

        if (isNaN(lat) || isNaN(lng)) {
            toast.error("Enter valid latitude and longitude");
            return;
        }

        const geocoder = new google.maps.Geocoder();

        geocoder.geocode(
            {
                location: { lat, lng },
                region: "IN",
            },
            (results, status) => {
                if (status !== "OK" || !results?.[0]) {
                    toast.error("Unable to validate location");
                    return;
                }

                const components =
                    results[0].address_components || [];

                const isValidLocation =
                    validateLocationByComponents(
                        components
                    );

                if (!isValidLocation) {
                    toast.error(
                        `Please select location inside ${selectedState}`
                    );
                    return;
                }

                const loc = { lat, lng };

                currentCenterRef.current = loc;

                setDisplayCoords(loc);

                setRouteEnabled(true);

                mapRef.current?.setCenter(loc);
                mapRef.current?.setZoom(18);

                reverseGeocode(lat, lng);
            }
        );
    };

    if (!isOpen) return null;

    const showLoader = !isLoaded || !showMap;

    return createPortal(
        <div className="fixed inset-0 z-[99] flex flex-col bg-white" style={{ height: "100dvh" }}>

            {/* Search Bar */}
            <div className="bg-white px-3 pt-4 pb-3 shadow-sm flex items-center gap-2 flex-shrink-0">
                <button
                    type="button"
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                >
                    <X className="w-5 h-5 text-gray-600" />
                </button>
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                    {isLoaded ? (
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search for area, street name…"
                            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-gray-50"
                        />
                    ) : (
                        <div className="w-full h-10 bg-gray-100 rounded-xl animate-pulse" />
                    )}
                </div>
                <div className="px-3 pb-2 flex gap-2">
                    <input
                        type="text"
                        step="any"
                        placeholder="Latitude"
                        value={manualLat}
                        onChange={(e) => setManualLat(e.target.value)}
                        className="flex-1 border rounded-lg px-3 py-2"
                    />

                    <input
                        type="text"
                        step="any"
                        placeholder="Longitude"
                        value={manualLng}
                        onChange={(e) => setManualLng(e.target.value)}
                        className="flex-1 border rounded-lg px-3 py-2"
                    />

                    <button
                        type="button"
                        onClick={handleLatLngSearch}
                        className="px-4 py-2 bg-orange-500 text-white rounded-lg"
                    >
                        Go
                    </button>
                </div>
            </div>

            {/* Map / Loader */}
            <div className="relative flex-1 min-h-0">
                {showLoader ? (
                    <MapLoader message={loaderMsg} />
                ) : (
                    <GoogleMap
                        mapContainerStyle={{ width: "100%", height: "100%" }}
                        center={mapInitCenter!}
                        onLoad={setMap}
                        zoom={18}
                        onClick={handleMapClick}
                        options={{
                            streetViewControl: false,
                            mapTypeControl: false,
                            fullscreenControl: false
                        }}
                    >
                        {/* {displayCoords && ( */}
                        <Marker
                            position={displayCoords}
                            icon={schoolLat != null ? {
                                url: homeIconUrl,
                                scaledSize: new window.google.maps.Size(36, 44),
                                anchor: new window.google.maps.Point(18, 44),
                            } : undefined}
                        />
                        {/* )} */}
                        {schoolLat != null && schoolLng != null && (
                            <Marker
                                position={{ lat: schoolLat, lng: schoolLng }}
                                icon={{
                                    url: schoolIconUrl,
                                    scaledSize: new window.google.maps.Size(36, 44),
                                    anchor: new window.google.maps.Point(18, 44),
                                }}
                                title={schoolName}
                            />
                        )}
                    </GoogleMap>
                )}

                {/* Fixed center pin — food-app style: map moves beneath, pin stays at screen center */}
                {/* {!showLoader && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className={`flex flex-col items-center transition-all duration-150 ${isDragging ? "-translate-y-5" : ""}`}>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-xl border-2 border-white transition-colors duration-150 ${isDragging ? "bg-orange-600 scale-110" : "bg-orange-500"}`}>
                <MapPin className="w-6 h-6 text-white fill-current" />
              </div>
              <div className={`w-1 h-3 bg-orange-500 rounded-full transition-opacity duration-150 ${isDragging ? "opacity-0" : "opacity-100"}`} />
              <div className={`w-4 h-1.5 bg-black/20 rounded-full blur-sm transition-all duration-150 ${isDragging ? "scale-150 opacity-0" : "scale-100 opacity-100"}`} />
            </div>
          </div>
        )} */}

                {/* My Location button */}
                {!showLoader && (
                    <button
                        type="button"
                        onClick={handleMyLocation}
                        disabled={locating}
                        className="absolute bottom-5 right-4 flex items-center gap-2 bg-white shadow-lg px-3.5 py-2.5 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 active:scale-95 disabled:opacity-70 transition-all border border-gray-100"
                    >
                        {locating ? (
                            <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
                        ) : (
                            <Navigation className="w-4 h-4 text-orange-500" />
                        )}
                        <span>{locating ? "Locating…" : "My Location"}</span>
                    </button>
                )}
            </div>

            {/* Bottom Sheet */}
            <div className="bg-white border-t border-gray-100 px-4 pt-4 pb-6 shadow-[0_-8px_24px_rgba(0,0,0,0.08)] flex-shrink-0">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MapPin className="w-5 h-5 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold text-orange-500 uppercase tracking-widest mb-0.5">
                            Location
                        </p>
                        {loadingAddress || isDragging ? (
                            <div className="space-y-1.5">
                                <div className="h-4 bg-gray-100 rounded-lg animate-pulse w-3/4" />
                                <div className="h-3 bg-gray-100 rounded-lg animate-pulse w-1/2" />
                            </div>
                        ) : address ? (
                            <>
                                <p className="text-sm font-bold text-gray-900 leading-snug">
                                    {addressMain || address}
                                </p>
                                {addressSub && (
                                    <p className="text-xs text-gray-500 mt-0.5">{addressSub}</p>
                                )}
                                {displayCoords && (
                                    <p className="text-[10px] text-gray-400 mt-1 font-medium">
                                        {displayCoords.lat}, {displayCoords.lng}
                                    </p>
                                )}
                            </>
                        ) : (
                            <p className="text-sm text-gray-400">
                                {showLoader ? "Waiting for location…" : "Move the map to select your location"}
                            </p>
                        )}
                    </div>
                </div>

                {schoolLat != null && schoolLng != null && displayCoords && routeEnabled && (
                    <div className="bg-orange-50 border border-orange-200 rounded-xl mb-3 px-3 py-2.5">
                        <div className="flex items-stretch gap-3">
                            {/* Route dots + dashed line */}
                            <div className="flex flex-col items-center gap-0.5 pt-0.5 flex-shrink-0">
                                <span className="w-3 h-3 rounded-full bg-green-500" />
                                <div className="flex-1 border-l-2 border-dashed border-orange-300 min-h-[16px]" />
                                <span className="w-3 h-3 rounded-full bg-brand-500" />
                            </div>
                            {/* Labels */}
                            <div className="flex-1 min-w-0 flex flex-col justify-between gap-1">
                                <span className="text-xs font-semibold text-gray-800 leading-tight">Home</span>
                                <span className="text-xs font-semibold text-gray-800 truncate leading-tight">{schoolName || "School"}</span>
                            </div>
                            {/* Distance + Duration */}
                            <div className="flex flex-col items-end justify-center flex-shrink-0">
                                {distance ? (
                                    <>
                                        <span className="text-sm font-bold text-orange-600 leading-tight">{distance}</span>
                                        {duration && <span className="text-xs text-gray-500 leading-tight">{duration}</span>}
                                    </>
                                ) : schoolDistance ? (
                                    <span className="text-sm font-bold text-orange-600">{schoolDistance}</span>
                                ) : (
                                    <span className="text-xs text-gray-400 flex items-center gap-1">
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Calculating…
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <button
                    type="button"
                    disabled={loadingAddress || !address || !displayCoords || showLoader}
                    onClick={() => {
                        // prevent invalid submit
                        if (
                            !displayCoords ||
                            !address
                        ) {
                            toast.error(
                                `Please select a valid location inside ${selectedState}`
                            );

                            return;
                        }

                        const geocoder =
                            new google.maps.Geocoder();

                        geocoder.geocode(
                            {
                                location: {
                                    lat: displayCoords.lat,
                                    lng: displayCoords.lng,
                                },
                                region: "IN",
                            },
                            (
                                results,
                                status
                            ) => {
                                if (
                                    status !== "OK" ||
                                    !results?.[0]
                                ) {
                                    toast.error(
                                        "Unable to validate location"
                                    );

                                    return;
                                }

                                const isValidLocation =
                                    validateLocationByComponents(
                                        results[0]
                                            .address_components ||
                                        []
                                    );

                                if (
                                    !isValidLocation
                                ) {
                                    toast.error(
                                        `Please select location inside ${selectedCity}, ${selectedState}`
                                    );

                                    return;
                                }

                                onConfirm({
                                    latitude:
                                        displayCoords.lat.toFixed(
                                            6
                                        ),
                                    longitude:
                                        displayCoords.lng.toFixed(
                                            6
                                        ),
                                    address,
                                    distanceKm:
                                        currentRoadDistanceRef.current ??
                                        undefined,
                                });
                            }
                        );
                    }}
                    className="w-full py-4 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:bg-orange-300 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                    <Check className="w-4 h-4" />
                    Confirm Location
                </button>
            </div>
        </div>,
        document.body
    );
}
